import { useMemo, useState } from 'react'
import moment from 'moment'
import { Select, DatePicker, Input, Divider, Button } from 'antd'

import { FormItemWrapper } from 'admin/lib/Forms'
import { Refresh } from 'admin/lib/Icons/Refresh'

import { Queries } from 'admin/lib/Api'
import { ResponsiveWrapper } from 'admin/lib/Common/StyledElements'
import { areAllAvailableSelected } from 'admin/lib/Functions/arrayObjectComparisonHelper'
import {
  availableStatuses,
  availableCountries,
  availableModules
} from 'admin/lib/InTray/FilterHelpers'

export function Filters({
  accountIds,
  projectIds,
  users,
  assignees,
  dispatch,
  handleRefreshRecords,
  disabled
}) {
  const [accountsSearchInput, setAccountsSearchInput] = useState('')
  const [projectsSearchInput, setProjectsSearchInput] = useState('')
  const [templatesSearchInput, setTemplatesSearchInput] = useState('')

  const { RangePicker } = DatePicker
  const { Option } = Select
  const { Search } = Input

  const accountMappings = Queries.useUserSelfAccountMappings()

  const accounts = useMemo(() => {
    const accs = {}
    if (accountMappings.data) {
      accountMappings.data.forEach(a => {
        const account_projects = []
        a.projects.map(p => account_projects.push(p.projectId))
        accs[a.accountId] = { name: a.name, projects: account_projects }
      })
    }
    return accs
  }, [accountMappings.data])

  const availableAccounts = useMemo(() => {
    const filtered = {}
    Object.entries(accounts).forEach(([k, v]) => {
      if (
        v.name.toLowerCase().indexOf(accountsSearchInput.toLowerCase()) >= 0
      ) {
        filtered[k] = v
      }
    })
    return filtered
  }, [accounts, accountsSearchInput])

  const projects = useMemo(() => {
    const prjs = {}
    if (accountMappings.data) {
      accountMappings.data.map(a =>
        a.projects.map(p => (prjs[p.projectId] = p.name))
      )
    }
    return prjs
  }, [accountMappings.data])

  const availableProjects = useMemo(() => {
    let available = {}
    if (accountIds.length === 0 || accountIds.length === accounts.length) {
      available = projects
    } else {
      accountIds.map(a =>
        accounts[a].projects.map(p => (available[p] = projects[p]))
      )
    }
    const filtered = {}
    Object.entries(available).forEach(([k, v]) => {
      if (v.toLowerCase().indexOf(projectsSearchInput.toLowerCase()) >= 0) {
        filtered[k] = v
      }
    })
    return filtered
  }, [accountIds, accounts, projects, projectsSearchInput])

  const availableAssignees = useMemo(() => {
    const otherAssigneeOptions = [
      { value: 'unassigned', label: 'Unassigned' },
      { value: 'backdated', label: 'Backdated' }
    ]
    let availableAssignees = []
    if (users && users.data) {
      availableAssignees = users.data
        .map(user => {
          return { value: user.displayName, label: user.displayName }
        })
        .filter(
          (value, index, self) =>
            index ===
            self.findIndex(
              t =>
                t.value === value.value && t.displayName === value.displayName
            )
        )
    }
    return [...otherAssigneeOptions, ...availableAssignees]
  }, [users])

  const templateNames = Queries.useTemplateNames()

  const availableTemplateNames = useMemo(() => {
    const availableTemplates = []
    if (templateNames.data) {
      templateNames.data.forEach(templateName => {
        availableTemplates.push(
          <Option key={templateName}>{templateName}</Option>
        )
      })
    }
    return [availableTemplates]
  }, [Option, templateNames.data])

  function onDateRangeChange(dates) {
    dispatch({ type: 'setDateRange', data: { dates: dates } })
  }

  function handleAccountIdsChange(value) {
    if (areAllAvailableSelected(value, availableAccounts)) {
      setAccountsSearchInput('')
    }
    let available = {}
    if (value.length === 0 || value.length === accounts.length) {
      available = projects
    } else {
      value.map(a =>
        accounts[a].projects.map(p => (available[p] = projects[p]))
      )
    }
    const newProjectIds = projectIds.filter(p =>
      Object.keys(available).includes(p)
    )
    dispatch({
      type: 'setAccountFilter',
      data: { accountIds: value, projectIds: newProjectIds }
    })
  }

  function handleProjectIdsChange(value) {
    if (areAllAvailableSelected(value, availableProjects)) {
      setProjectsSearchInput('')
    }
    dispatch({ type: 'setProjectFilter', data: { projectIds: value } })
  }

  function handleStatusesChange(value) {
    dispatch({ type: 'setStatusFilter', data: { statuses: value } })
  }

  function handleModulesChange(value) {
    dispatch({ type: 'setModulesFilter', data: { modules: value } })
  }

  function handleSelectAllAccounts() {
    const merged_array = Object.keys(availableAccounts)
    accountIds.forEach(a => {
      if (merged_array.indexOf(a) === -1) {
        merged_array.push(a)
      }
    })
    handleAccountIdsChange(merged_array)
  }
  function handleClearAllAccounts() {
    setAccountsSearchInput('')
    const cleared_array = []
    accountIds.forEach(a => {
      if (Object.keys(availableAccounts).indexOf(a) === -1) {
        cleared_array.push(a)
      }
    })
    handleAccountIdsChange(cleared_array)
  }

  function handleSelectAllAssignedTo() {
    const merged_array = []
    availableAssignees.forEach(assignee => {
      merged_array.push(assignee.value)
    })
    handleAssignedToChange(merged_array)
  }

  function handleClearAllAssignedTo() {
    handleAssignedToChange([])
  }

  function onCompanyNameSearch(value) {
    dispatch({ type: 'setCompanyNameFilter', data: { companyName: value } })
  }

  function handleAssignedToChange(value) {
    dispatch({ type: 'setAssigneeFilter', data: { assignees: value } })
  }

  function handleTemplateNamesChange(value) {
    dispatch({ type: 'setTemplateNamesFilter', data: { templateNames: value } })
  }

  function handleCountriesChange(value) {
    dispatch({ type: 'setCountriesFilter', data: { countries: value } })
  }

  function onMovementIdSearch(value) {
    dispatch({ type: 'setMovementIdFilter', data: { movementId: value } })
  }

  const filterOption = (input, option) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  return (
    <div className="filter-section">
      <ResponsiveWrapper style={{ gap: '0.5rem' }}>
        <div id="filterBar" className="filter-bar">
          <FormItemWrapper style={{ flexGrow: 1 }}>
            <label htmlFor="filterBarCreatedAtRange">Created at</label>
            <RangePicker
              showTime
              minuteStep={5}
              id="filterBarCreatedAtRange"
              defaultValue={[
                moment().subtract(14, 'day').startOf('day'),
                moment().endOf('day')
              ]}
              format="DD/MM/YYYY HH:mm"
              presets={[
                {
                  label: 'Today',
                  value: [moment().startOf('day'), moment().endOf('day')]
                },
                {
                  label: 'Last 30 days',
                  value: [
                    moment().subtract(30, 'day').startOf('day'),
                    moment().endOf('day')
                  ]
                }
              ]}
              onChange={onDateRangeChange}
              className="filter-large"
            />
          </FormItemWrapper>
          <FormItemWrapper style={{ flexGrow: 1 }}>
            <label htmlFor="filterBarAccounts">Accounts</label>
            <Select
              id="filterBarAccounts"
              mode="multiple"
              allowClear
              placeholder="Please select"
              onChange={handleAccountIdsChange}
              value={accountIds.sort((keyA, keyB) =>
                accounts[keyA].name.localeCompare(accounts[keyB].name)
              )}
              className="scrollable-select filter-large"
              searchValue={accountsSearchInput}
              filterOption={() => true}
              maxCount={200}
              maxTagCount="responsive"
              onSearch={value => {
                setAccountsSearchInput(value)
              }}
              onOpenChange={open => {
                if (!open) {
                  setAccountsSearchInput('')
                }
              }}
              popupRender={menu => (
                <div>
                  {menu}
                  <Divider style={{ margin: '4px 0' }} />{' '}
                  {!areAllAvailableSelected(accountIds, availableAccounts) && (
                    <Button
                      type="primary"
                      block
                      onClick={handleSelectAllAccounts}
                      style={{
                        margin: '6px 12px',
                        width: 'calc(100% - 24px)'
                      }}
                    >
                      Select All
                    </Button>
                  )}
                  {areAllAvailableSelected(accountIds, availableAccounts) && (
                    <Button
                      type="primary"
                      block
                      onClick={handleClearAllAccounts}
                      style={{
                        margin: '6px 12px',
                        width: 'calc(100% - 24px)'
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              )}
            >
              {Object.keys(availableAccounts)
                .sort((keyA, keyB) =>
                  accounts[keyA].name.localeCompare(accounts[keyB].name)
                )
                .map(k => (
                  <Option key={k}>{accounts[k].name}</Option>
                ))}
            </Select>
          </FormItemWrapper>
          <FormItemWrapper style={{ flexGrow: 1 }}>
            <label htmlFor="filterBarProjects">Projects</label>
            <Select
              id="filterBarProjects"
              mode="multiple"
              allowClear
              placeholder="Please select"
              onChange={handleProjectIdsChange}
              value={projectIds.sort((keyA, keyB) =>
                projects[keyA].localeCompare(projects[keyB])
              )}
              className="scrollable-select filter-large"
              searchValue={projectsSearchInput}
              filterOption={() => true}
              maxCount={200}
              maxTagCount="responsive"
              onSearch={value => {
                setProjectsSearchInput(value)
              }}
              onOpenChange={open => {
                if (!open) {
                  setProjectsSearchInput('')
                }
              }}
            >
              {Object.keys(availableProjects)
                .sort((keyA, keyB) =>
                  projects[keyA].localeCompare(projects[keyB])
                )
                .map(k => (
                  <Option key={k}>{projects[k]}</Option>
                ))}
            </Select>
          </FormItemWrapper>
          <FormItemWrapper>
            <label htmlFor="filterBarRecordType">Record type</label>
            <Select
              id="filterBarRecordType"
              mode="multiple"
              allowClear
              placeholder="Please select"
              onChange={handleModulesChange}
              className="filter-medium"
            >
              {availableModules}
            </Select>
          </FormItemWrapper>
        </div>
        <div id="filterBar2" className="filter-bar">
          <FormItemWrapper style={{ flexGrow: 1 }}>
            <label htmlFor="filterBarCompanyName">Supplier / Carrier</label>
            <Search
              id="filterBarCompanyName"
              allowClear
              placeholder="Enter text"
              onSearch={onCompanyNameSearch}
              className="filter-large"
              disabled={disabled}
            />
          </FormItemWrapper>
          <FormItemWrapper style={{ flexGrow: 1 }}>
            <label htmlFor="filterBarMovementId">Movement ID</label>
            <Search
              id="filterBarMovementId"
              allowClear
              placeholder="Enter text"
              onSearch={onMovementIdSearch}
              className="filter-large"
              disabled={disabled}
            />
          </FormItemWrapper>
          <FormItemWrapper style={{ flexGrow: 1 }}>
            <label htmlFor="filterBarAssignee">Assigned to</label>
            <Select
              id="filterBarAssignee"
              mode="multiple"
              allowClear
              placeholder="Please select"
              onChange={handleAssignedToChange}
              value={assignees}
              showSearch={true}
              filterOption={filterOption}
              className="scrollable-select filter-large"
              options={availableAssignees}
              maxTagCount="responsive"
              popupRender={menu => (
                <div>
                  {menu}
                  <Divider style={{ margin: '4px 0' }} />{' '}
                  {!areAllAvailableSelected(assignees, availableAssignees) && (
                    <Button
                      type="primary"
                      block
                      onClick={handleSelectAllAssignedTo}
                      style={{
                        margin: '6px 12px',
                        width: 'calc(100% - 24px)'
                      }}
                    >
                      Select All
                    </Button>
                  )}
                  {areAllAvailableSelected(assignees, availableAssignees) && (
                    <Button
                      type="primary"
                      block
                      onClick={handleClearAllAssignedTo}
                      style={{
                        margin: '6px 12px',
                        width: 'calc(100% - 24px)'
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              )}
            />
          </FormItemWrapper>
          <FormItemWrapper>
            <label htmlFor="filterBarStatus">Status</label>
            <Select
              id="filterBarStatus"
              mode="multiple"
              allowClear
              placeholder="Please select"
              onChange={handleStatusesChange}
              className="filter-medium"
            >
              {availableStatuses}
            </Select>
          </FormItemWrapper>
        </div>
        <div id="filterBar3" className="filter-bar">
          <FormItemWrapper style={{ flexGrow: 1 }}>
            <label htmlFor="filterBarTemplateNames">Templates</label>
            <Select
              id="filterBarTemplateNames"
              mode="multiple"
              allowClear
              placeholder="Please select"
              onChange={handleTemplateNamesChange}
              className="filter-large"
              searchValue={templatesSearchInput}
              onSearch={value => {
                setTemplatesSearchInput(value)
              }}
              onOpenChange={open => {
                if (!open) {
                  setTemplatesSearchInput('')
                }
              }}
            >
              {availableTemplateNames}
            </Select>
          </FormItemWrapper>
          <FormItemWrapper style={{ flexGrow: 1 }}>
            <label htmlFor="filterBarStatus">Country</label>
            <Select
              id="filterBarCountry"
              mode="multiple"
              allowClear
              placeholder="Please select"
              onChange={handleCountriesChange}
              className="filter-medium"
            >
              {availableCountries}
            </Select>
          </FormItemWrapper>
          <FormItemWrapper style={{ flexGrow: 10 }}>
            <span className="filter-large">&nbsp;</span>
          </FormItemWrapper>
          <FormItemWrapper style={{ flexGrow: 0 }}>
            <div>&nbsp;</div>
            <Button
              type="primary"
              icon={<Refresh />}
              onClick={handleRefreshRecords}
            >
              Apply filters
            </Button>
          </FormItemWrapper>
        </div>
      </ResponsiveWrapper>
    </div>
  )
}
